<template>
	<div class="loan-documents-tab container-fluid px-0">
		<div
			class="d-flex justify-content-between align-items-center mb-3 flex-wrap border-bottom pb-2"
		>
			<h6 class="m-0 fw-bold akkurate-green">Documents</h6>
			<div>
				<input
					ref="fileInput"
					type="file"
					class="d-none"
					accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
					@change="onFileSelected"
				/>
				<b-button
					type="button"
					class="akkurate-green-btn btn-size text-white text-uppercase"
					:disabled="!effectiveLoanRef || uploadLoading"
					@click="$refs.fileInput.click()"
				>
					<font-awesome-icon class="me-1" :icon="['fas', 'upload']" />
					Upload
				</b-button>
			</div>
		</div>

		<div v-if="listError" class="alert alert-danger py-2 small mb-2">
			{{ listError }}
		</div>

		<div
			v-if="listLoading && items.length === 0"
			class="text-center text-muted py-4"
		>
			<b-spinner small class="align-middle me-2" />
			Loading documents…
		</div>

		<div
			v-else-if="!listLoading && items.length === 0 && !listError"
			class="akkurate-small"
		>
			<p class="text-muted small mb-2">
				No documents uploaded yet. Showing sample placeholders below.
			</p>
			<div class="row akkurate-skyblue">
				<div
					class="col-6 col-md-4 col-lg-3 mb-3"
					style="max-height: 389px"
					v-for="f in dummyAttachedFiles"
					:key="'dummy-' + f.id"
				>
					<a
						href="#"
						class="custom-pointer d-block"
						@click.prevent="openDummyPreview(f.file)"
					>
						<attachment-preview :file="f.file" />
					</a>
				</div>
			</div>
		</div>

		<div v-else-if="items.length > 0" class="row akkurate-small">
			<div
				v-if="listLoading && items.length > 0"
				class="col-12 small text-muted mb-2"
			>
				<b-spinner small class="align-middle me-1" /> Updating…
			</div>
			<div
				v-for="(att, idx) in items"
				:key="attachmentKey(att, idx)"
				class="col-6 col-md-4 col-lg-3 mb-3"
			>
				<div class="border rounded h-100 p-2 bg-white">
					<a
						href="#"
						class="d-block text-decoration-none text-dark"
						@click.prevent="openPreview(att)"
					>
						<div
							class="bg-light rounded mb-2 d-flex align-items-center justify-content-center overflow-hidden"
							style="height: 140px"
						>
							<b-img
								v-if="isImageUrl(att.preview_url)"
								:src="att.preview_url"
								fluid
								class="object-fit-contain"
								:alt="displayName(att)"
							/>
							<span v-else class="text-muted small text-center p-2">
								<font-awesome-icon
									:icon="['fas', 'file-pdf']"
									size="2x"
									class="mb-1 d-block"
								/>
								{{ displayName(att) }}
							</span>
						</div>
						<div class="small text-truncate" :title="displayName(att)">
							{{ displayName(att) }}
						</div>
					</a>
					<div class="text-muted extra-small mt-1" v-if="formatUser(att)">
						{{ formatUser(att) }}
					</div>
					<div class="mt-1">
						<b-button
							size="sm"
							variant="link"
							class="p-0 me-2 akkurate-green"
							:href="att.preview_url"
							target="_blank"
							rel="noopener noreferrer"
						>
							Open
						</b-button>
					</div>
				</div>
			</div>
		</div>

		<b-modal
			id="bv-modal-loan-document-preview"
			size="xl"
			hide-footer
			@hidden="selectedFile = null"
		>
			<template #modal-title>
				<h6 class="akkurate-green fw-bold mb-0">Document preview</h6>
			</template>
			<div v-if="selectedFile">
				<attachment-preview :file="selectedFile" />
			</div>
		</b-modal>
	</div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import { apiErrorMessage } from "@/utils/apiErrorMessage.js";
import { loanAttachmentsPath } from "@/utils/loanViewApiPaths.js";
import AttachmentPreview from "@/views/components/AttachmentPreview.vue";

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXT = ["png", "jpg", "jpeg", "pdf"];

export default {
	name: "LoanDocumentsTab",
	components: { AttachmentPreview },
	props: {
		/** Numeric loan id or loan_number string */
		loanRef: {
			type: [Number, String],
			default: null,
		},
	},
	data() {
		return {
			items: [],
			listLoading: false,
			listError: null,
			uploadLoading: false,
			/** URL string or File — shown in preview modal when set */
			selectedFile: null,
			/** Placeholder tiles when the API returns no attachments */
			dummyAttachedFiles: [
				{
					id: 1,
					file: "https://d1vzi28wh99zvq.cloudfront.net/images/3605/242174.jpg",
				},
				{
					id: 2,
					file: "https://d1vzi28wh99zvq.cloudfront.net/images/3605/242174.jpg",
				},
				{
					id: 3,
					file: "https://d1vzi28wh99zvq.cloudfront.net/images/3605/242174.jpg",
				},
				{
					id: 4,
					file: "https://d1vzi28wh99zvq.cloudfront.net/images/3605/242174.jpg",
				},
			],
		};
	},
	computed: {
		effectiveLoanRef() {
			if (this.loanRef == null || this.loanRef === "") {
				return null;
			}
			if (typeof this.loanRef === "number") {
				return Number.isFinite(this.loanRef) && this.loanRef > 0
					? this.loanRef
					: null;
			}
			const s = String(this.loanRef).trim();
			return s ? s : null;
		},
	},
	watch: {
		effectiveLoanRef: {
			immediate: true,
			handler(ref) {
				if (ref != null) {
					this.fetchAttachments();
				} else {
					this.items = [];
					this.listError = null;
				}
			},
		},
	},
	methods: {
		attachmentKey(att, idx) {
			if (att && att.id != null) {
				return String(att.id);
			}
			return `att-${idx}-${att?.preview_url || ""}`;
		},
		displayName(att) {
			if (!att || typeof att !== "object") {
				return "File";
			}
			const n =
				att.original_name ||
				att.original_filename ||
				att.file_name ||
				att.name ||
				"";
			if (n) {
				return String(n);
			}
			if (att.preview_url) {
				try {
					const u = new URL(att.preview_url, window.location.origin);
					const seg = u.pathname.split("/").filter(Boolean);
					return seg.length ? seg[seg.length - 1] : "File";
				} catch (e) {
					return "File";
				}
			}
			return "File";
		},
		formatUser(att) {
			const u = att && att.user;
			if (!u || typeof u !== "object") {
				return "";
			}
			const fn = u.first_name || "";
			const sn = u.surname || "";
			const name = `${fn} ${sn}`.trim();
			return name || (u.id != null ? `User #${u.id}` : "");
		},
		isImageUrl(url) {
			if (!url || typeof url !== "string") {
				return false;
			}
			const lower = url.split("?")[0].toLowerCase();
			return /\.(png|jpe?g)$/i.test(lower);
		},
		normalizeListResponse(res) {
			const root = res && res.data;
			if (!root) {
				return [];
			}
			if (Array.isArray(root.data)) {
				return root.data;
			}
			if (Array.isArray(root)) {
				return root;
			}
			if (root.data && Array.isArray(root.data.data)) {
				return root.data.data;
			}
			return [];
		},
		async fetchAttachments() {
			const ref = this.effectiveLoanRef;
			if (ref == null) {
				return;
			}
			this.listLoading = true;
			this.listError = null;
			try {
				const response = await ApiService.get(
					loanAttachmentsPath(ref),
				);
				this.items = this.normalizeListResponse(response);
			} catch (error) {
				this.items = [];
				this.listError = apiErrorMessage(
					error,
					"Could not load loan documents.",
				);
			} finally {
				this.listLoading = false;
			}
		},
		openPreview(att) {
			const url =
				att &&
				(att.preview_url ||
					(typeof att.file === "string" ? att.file : ""));
			if (!url || typeof url !== "string") {
				return;
			}
			this.selectedFile = url;
			this.$bvModal.show("bv-modal-loan-document-preview");
		},
		openDummyPreview(fileUrl) {
			if (!fileUrl || typeof fileUrl !== "string") {
				return;
			}
			this.selectedFile = fileUrl;
			this.$bvModal.show("bv-modal-loan-document-preview");
		},
		attachmentExtension(file) {
			const name = (file && file.name) || "";
			const parts = name.split(".");
			if (parts.length < 2) {
				return "";
			}
			return parts.pop().toLowerCase();
		},
		async onFileSelected(event) {
			const input = event.target;
			const file = input.files && input.files[0];
			input.value = "";
			const ref = this.effectiveLoanRef;
			if (!file || ref == null) {
				return;
			}
			const ext = this.attachmentExtension(file);
			if (!ALLOWED_ATTACHMENT_EXT.includes(ext)) {
				swal.fire({
					icon: "error",
					text: "Allowed types: PNG, JPG, JPEG, PDF.",
					confirmButtonText: "OK",
					buttonsStyling: false,
					customClass: {
						confirmButton:
							"btn btn-size akkurate-green-btn modal-btn-width text-white me-2",
					},
				});
				return;
			}
			if (file.size > MAX_ATTACHMENT_BYTES) {
				swal.fire({
					icon: "error",
					text: "File must be 5 MB or smaller.",
					confirmButtonText: "OK",
					buttonsStyling: false,
					customClass: {
						confirmButton:
							"btn btn-size akkurate-green-btn modal-btn-width text-white me-2",
					},
				});
				return;
			}
			this.uploadLoading = true;
			this.$Progress.start();
			try {
				const data = new FormData();
				data.append("attachment", file);
				await ApiService.post(loanAttachmentsPath(ref), data);
				await this.fetchAttachments();
			} catch (error) {
				swal.fire({
					icon: "error",
					text: apiErrorMessage(error, "Upload failed."),
					confirmButtonText: "OK",
					buttonsStyling: false,
					customClass: {
						confirmButton:
							"btn btn-size akkurate-green-btn modal-btn-width text-white me-2",
					},
				});
			} finally {
				this.uploadLoading = false;
				this.$Progress.finish();
			}
		},
	},
};
</script>

<style scoped>
.extra-small {
	font-size: 0.75rem;
}
.object-fit-contain {
	object-fit: contain;
	max-height: 160px;
}
</style>
